import { Component, effect, inject, untracked, viewChild } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { withAddAddress } from '@libs/mtjr/shared/generated/entities/user/user-store-features';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { TranslatePipe } from '@ngx-translate/core';
import { LocationCoordinates, LocationMapComponent } from './location-map.component';

import { BASE_CONFIG } from '@libs/core/base-config';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { errorState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { addressCreateUtils } from '@libs/mtjr/shared/generated/forms';
import { patchState, signalStore, withMethods } from '@ngrx/signals';

export const AddressCreateStore = signalStore(
  withAddAddress(),
  withMethods((store) => ({
    setFailure(failure: Failure) {
      patchState(store, { addAddressState: errorState(failure) });
    },
  })),
);

@Component({
  selector: 'mac-create-address',
  providers: [AddressCreateStore],
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    VxTextFieldComponent,
    ReactiveFormsModule,
    FormsModule,
    VxSubmitButtonComponent,
    TranslatePipe,
    VxErrorBarComponent,
    IonText,
    IonBackButton,
    LocationMapComponent,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-title>{{ 'ADDRESS.CREATE_NEW_ADDRESS' | translate }}</ion-title>
        <ion-buttons slot="start">
          <ion-back-button defaultHref="/account/addresses" [text]="'COMMON.BACK' | translate" />
        </ion-buttons>

        <ion-buttons slot="end">
          <mtjr-submit-button
            data-cy="address-submit-button"
            fill="none"
            class="text-primary"
            (click)="save()"
            [label]="'COMMON.SAVE' | translate"
            [state]="editStore.addAddressState()"
          />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="flex">
        <div class="mx-auto w-full max-w-[500px] pt-4">
          <form [formGroup]="createUtils.form" (ngSubmit)="save()">
            <div class="mt-2 px-5 py-3">
              <fieldset>
                <mtjr-text-field
                  [field]="createUtils.form.controls.label"
                  [label]="'ADDRESS.NAME' | translate"
                  [placeholder]="'ADDRESS.NAME_EXAMPLE' | translate"
                />
                <div class="my-3"></div>
                <div class="my-3"></div>
                <mtjr-text-field [field]="createUtils.form.controls.street" [label]="'ADDRESS.ADDRESS' | translate" />
                <div class="my-3"></div>
                <mtjr-text-field [field]="createUtils.form.controls.phone" [label]="'ADDRESS.PHONE' | translate" />
                <div class="my-3"></div>
                <mtjr-text-field [field]="createUtils.form.controls.phone2" [label]="'ADDRESS.PHONE2' | translate" />

                <div class="mt-3 mb-2 py-2 flex items-center justify-between">
                  <ion-text>{{ 'ADDRESS.LOCATION' | translate }}</ion-text>
                  @if (createUtils.form.controls.longitude.value && createUtils.form.controls.latitude.value) {
                    <ion-text color="success">
                      {{ 'ADDRESS.LOCATION_SELECTED' | translate }}
                    </ion-text>
                  }
                </div>

                <!-- Map Component -->
                <mtjr-location-map [coordinates]="getCoordinates()" (locationSelected)="onLocationSelected($event)" />
              </fieldset>
            </div>
            <div class="my-2">
              <mtjr-error-bar [failure]="editStore.addAddressFailure()" />
            </div>

            <!-- <div class="mx-4">
              <mtjr-submit-button
                data-cy="address-submit-button"
                [label]="'ADDRESS.CREATE_NEW_ADDRESS' | translate"
                [state]="editStore.addAddressState()"
              />
            </div> -->
          </form>
        </div>
      </div>
    </ion-content>
  `,
})
export class CreateAddressPage {
  mapComponent = viewChild.required(LocationMapComponent);

  editStore = inject(AddressCreateStore);
  userStore = inject(UserStore);
  route = inject(ActivatedRoute);
  createUtils = addressCreateUtils(inject(NonNullableFormBuilder));
  router = inject(Router);
  alerts = inject(AppAlerts);
  isVendor = inject(BASE_CONFIG).flavor === 'vendors';

  async back() {
    const url = this.isVendor ? '/main/account/addresses' : '/account/addresses';
    this.router.navigate([url], {
      queryParams: this.route.snapshot.queryParams,
      relativeTo: this.route,
    });
  }

  constructor() {
    this.createUtils.form.reset();
    effect(async () => {
      if (this.editStore.addAddressState().success) {
        this.back();
        untracked(() => {
          this.userStore.loggedInUser();
        });
      }
    });
  }

  getCoordinates(): LocationCoordinates | null {
    const lat = this.createUtils.form.controls.latitude.value;
    const lng = this.createUtils.form.controls.longitude.value;

    if (lat && lng) {
      return { latitude: lat, longitude: lng };
    }
    return null;
  }

  async onLocationSelected(coordinates: LocationCoordinates) {
    this.createUtils.form.patchValue({
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    });

    // Perform reverse geocoding to get address text
    try {
      const address = await this.reverseGeocode(coordinates.latitude, coordinates.longitude);
      if (address) {
        this.createUtils.form.patchValue({
          internalAddress: address,
        });
        this.createUtils.form.controls.internalAddress.markAsDirty();
      }
    } catch (error) {
      console.warn('Failed to get address from coordinates:', error);
    }

    this.alerts.successSnackbarTR('ADDRESS.LOCATION_SELECTED', { position: 'bottom' });
  }

  private async reverseGeocode(lat: number, lng: number): Promise<string | null> {
    console.log('reverseGeocode...');

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=ar`,
      );

      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }

      const data = await response.json();

      console.log(data.address);

      // Extract meaningful address components
      const address = data.address;
      if (!address) return null;

      // Build address string from components
      const addressParts = new Set<string>();

      // Add city or town
      // if (address.city) addressParts.add(address.city);
      // if (address.state) addressParts.add(address.state);
      if (address.town) addressParts.add(address.town);
      if (address.city_district) addressParts.add(address.city_district);
      if (address.village) addressParts.add(address.village);
      if (address.suburb) addressParts.add(address.suburb);
      if (address.road) addressParts.add(address.road);

      return Array.from(addressParts).join(', ') || data.display_name || null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  }

  save() {
    if (this.createUtils.form.invalid) {
      return this.createUtils.form.markAllAsTouched();
    }
    const result = this.createUtils.parse();
    if (!result.success) {
      this.editStore.setFailure(new LocalFailure());
      this.createUtils.form.markAllAsTouched();
      return;
    }

    this.editStore.addAddress(result.data);
  }
}
